import React, { useState, useEffect } from "react";

/**
 * BackdatedConfirmModal — Phase 3E-9Q
 *
 * Mandatory guard rail before any pricing-margin action whose
 * effective_date is BEFORE today. Prevents accidental backdated
 * financial changes (see 2026-09-01 "Pipeline 2 Esso 3" incident).
 *
 * Contract:
 *   - Rendered ONLY when `effectiveDate < today` (parent decides).
 *   - Confirm button is disabled until the checkbox is checked.
 *   - Destructive button label is explicit ("Apply Backdated Margins",
 *     never "OK" or "Apply") so muscle-memory clicks don't slip through.
 *   - Optional `latestExistingDate` prop shows an enhanced warning
 *     when the backdated change would land BEFORE the most-recent
 *     applicable rule (i.e., would rewrite already-current pricing).
 *
 * Props:
 *   open              – bool
 *   onCancel          – () => void
 *   onConfirm         – () => void  (parent calls the API with confirm_backdated=true)
 *   effectiveDate     – "YYYY-MM-DD"  (the backdated date the operator picked)
 *   customerCount     – number       (customers affected, for bulk ops)
 *   ruleCount         – number       (margin definitions / rules affected)
 *   contextLabel      – string       ("Apply pricing group 'X'", "Upload 12 margin rules", …)
 *   latestExistingDate – "YYYY-MM-DD" | undefined  (enables extra warning line)
 *   confirmLabel      – string       (default "Apply Backdated Margins")
 *   busy              – bool         (disables the confirm button while API pending)
 */
export default function BackdatedConfirmModal({
  open,
  onCancel,
  onConfirm,
  effectiveDate,
  customerCount,
  ruleCount,
  contextLabel,
  latestExistingDate,
  confirmLabel = "Apply Backdated Margins",
  busy = false,
}) {
  const [acked, setAcked] = useState(false);
  // Reset on open so a stale ack from a previous invocation can't leak.
  useEffect(() => { if (open) setAcked(false); }, [open]);
  if (!open) return null;

  const today = new Date().toISOString().slice(0, 10);
  const disabled = busy || !acked;
  const shadowsNewer = latestExistingDate && effectiveDate < latestExistingDate;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      data-testid="backdated-modal-backdrop"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="bg-white w-full max-w-lg border-2 border-rose-500 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="backdated-modal"
      >
        <div className="p-4 border-b-2 border-rose-500 bg-rose-50">
          <div className="text-xs font-mono uppercase text-rose-700 tracking-widest mb-1">
            Backdated Pricing Change
          </div>
          <h3 className="text-lg font-semibold text-rose-900">
            Confirm past-dated margin application
          </h3>
        </div>
        <div className="p-4 space-y-3 text-sm text-zinc-800">
          {contextLabel && (
            <div data-testid="backdated-context" className="text-zinc-600">
              {contextLabel}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 border border-zinc-200 rounded p-3 bg-zinc-50">
            <div>
              <div className="overline text-zinc-500 mb-1">You are applying effective</div>
              <div className="font-mono text-base text-rose-700" data-testid="backdated-eff-date">
                {effectiveDate}
              </div>
            </div>
            <div>
              <div className="overline text-zinc-500 mb-1">Today is</div>
              <div className="font-mono text-base" data-testid="backdated-today">
                {today}
              </div>
            </div>
          </div>
          <div className="text-zinc-700">
            This may change historical customer pricing and reports.
          </div>
          {(customerCount != null || ruleCount != null) && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2 font-mono">
              Affected:{" "}
              {customerCount != null && (
                <span data-testid="backdated-customer-count">
                  {customerCount} customer{customerCount === 1 ? "" : "s"}
                </span>
              )}
              {customerCount != null && ruleCount != null && " · "}
              {ruleCount != null && (
                <span data-testid="backdated-rule-count">
                  {ruleCount} margin definition{ruleCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )}
          {shadowsNewer && (
            <div
              className="text-sm bg-rose-100 border border-rose-300 rounded p-2 text-rose-900"
              data-testid="backdated-shadows-newer"
            >
              <strong>Warning — precedes an existing rule.</strong>{" "}
              One or more customers already have newer margin rules
              (most recent: <span className="font-mono">{latestExistingDate}</span>).
              This backdated change may alter historical pricing precedence.
            </div>
          )}
          <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acked}
              onChange={(e) => setAcked(e.target.checked)}
              className="mt-1"
              data-testid="backdated-ack-checkbox"
            />
            <span className="text-sm text-zinc-800">
              I understand this will apply pricing to a past effective date
              and may change historical reports.
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200 bg-zinc-50">
          <button
            className="gf-btn gf-btn-outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="backdated-cancel"
          >
            Cancel
          </button>
          <button
            className="gf-btn"
            style={{
              background: disabled ? "#f5d0d0" : "#b91c1c",
              color: disabled ? "#a0a0a0" : "#ffffff",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            onClick={onConfirm}
            disabled={disabled}
            data-testid="backdated-confirm"
          >
            {busy ? "Applying…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small utility so callers can compute "is this backdated?" consistently.
// Uses the browser's local-calendar today for the comparison — matches
// how the operator thinks about "today". The backend re-derives it in UTC
// so a client-side false positive/negative is still gated server-side.
export function isBackdated(effectiveDate) {
  if (!effectiveDate) return false;
  const today = new Date().toISOString().slice(0, 10);
  return effectiveDate < today;
}
